import type { HumanoidAppearance } from '../GhostRenderer';
import { npc } from './npc';

/** The orphan starter_zone cast — not reachable in normal play (see CoilNpcAppearances.ts). */
export const STARTER_ZONE_CAST: Record<string, HumanoidAppearance> = {
  // ══════════════════════ Starter-zone cast (orphan zone, 2026-07-07) ══════════════════════
  // grant, guide_rafael, guard_captain, villager_bob, blacksmith_erik,
  // arcanist_luna, hunter_kael, shadow_dealer — content/npcs/starter_zone.toml.
  // No biome gradient; each design pulls from role + dialogue instead.

  // ── Grant the Veteran — Trainer (grizzled, weathered, hearty) ──
  // Big, scarred, greying combat trainer. bodySize:'large' + a scar are the
  // "decades of fighting" tell; short iron-grey beard (not old_ferris's full
  // salt-white) + oxblood battle-leathers keep him his own person.
  grant: npc({
    shape: 'wide',
    bodySize: 'large',
    color: '#a67c52', // weathered olive-brown, sun-toughened veteran skin
    eyes: 'round',
    mouth: 'grin', // hearty, eager to put a student through their paces
    faceMarking: 'scar',
    humanoid: {
      hair: { style: 'thin', color: '#9a9690' }, // grey, receding
      beard: { style: 'short', color: '#6a5a48' }, // iron-grey-brown, close-trimmed
      garment: { kind: 'tunic', color: '#7a3530', trimColor: '#3a2018' }, // oxblood battle-leathers
    },
  }),

  // ── Guard Captain — Guard (stern, regulation, on-duty) ──
  // Clean-shaven, black-haired, angry-eyed authority figure — the direct
  // "different individual, not a Grant palette-swap" foil: normal body size
  // (not Grant's 'large'), no beard, no scar, a navy officer's cloak Grant
  // never wears, cool steel-blue uniform instead of Grant's warm oxblood.
  guard_captain: npc({
    shape: 'wide',
    color: '#c9a17e',
    eyes: 'angry',
    mouth: 'neutral', // "Halt! State your business." / "Move along, citizen."
    humanoid: {
      hair: { style: 'short', color: '#2a2a2a' }, // regulation-short black hair
      cloak: { color: '#2a3548', claspColor: '#c8a840' }, // navy mantle, brass clasp — rank marker
      garment: { kind: 'tunic', color: '#4a5568', trimColor: '#aab0ba' }, // steel-blue uniform, silver trim
      art: { light: 'lamp', role: 'guard' },
    },
  }),

  // ── Villager Bob — Civilian (soft, sunny, unremarkable and proud of it) ──
  // The "softest" character by design (no quests, no gear) — personality
  // comes from blush + a big genuine grin rather than any prop, matching his
  // easygoing "beautiful day, isn't it?" dialogue. Patched homespun reads
  // working-class-content, not poor-and-grim.
  villager_bob: npc({
    shape: 'round',
    color: '#dba97e', // fair, ruddy — outdoors a lot, sunburns easy
    eyes: 'happy',
    mouth: 'grin',
    blush: true,
    humanoid: {
      hair: { style: 'short', color: '#8a5a2e' },
      garment: { kind: 'tunic', color: '#7a8a72', trimColor: '#8b6a4a' }, // faded homespun green-grey, patched brown trim
    },
  }),

  // ── Guide Rafael — Guide (energetic, welcoming, prepared) ──
  // The other "soft" character — an onboarding NPC that could easily default
  // to bland. A wink (unique among all 15) + trailblazer green + a
  // travel-ready satchel sell "enthusiastic new-adventurer scoutmaster"
  // without leaning on Sophia's already-claimed map-peek satchel.
  guide_rafael: npc({
    shape: 'slim',
    color: '#c8a074',
    eyes: 'winking',
    mouth: 'smile',
    humanoid: {
      hair: { style: 'short', color: '#5a3a20' },
      garment: { kind: 'tunic', color: '#2f6b4a', trimColor: '#c9a870' }, // trailblazer green, khaki trim
      props: [{ kind: 'satchel', color: '#8a5a2e' }],
    },
  }),

  // ── Blacksmith Erik — Shop (brawny, forge-flushed, proud) ──
  // Vendor family #1 of 4: class signature = an iron-grey smith's apron over
  // a rust work-shirt (echoes merchant_maria's apron-as-identifier precedent,
  // but deliberately NOT pocketBottles — a smith doesn't sell potions).
  blacksmith_erik: npc({
    shape: 'wide',
    color: '#c47850', // ruddy, forge-flushed skin
    eyes: 'round',
    mouth: 'grin', // "I've got the best iron in town!"
    humanoid: {
      hair: { style: 'short', color: '#3a2a1a' },
      beard: { style: 'short', color: '#3a2a1a' },
      garment: { kind: 'apron', color: '#4a4a48', underColor: '#a85838', trimColor: '#2a2a28' }, // iron-grey apron, rust-orange work-shirt
      art: { light: 'lamp', role: 'merchant' },
    },
  }),

  // ── Arcanist Luna — Shop (pale, otherworldly, mystic) ──
  // Vendor family #2 of 4: class signature = star-shaped eyes (unique among
  // all 15) + `glow:true` (also unique — no Coil NPC glows) + a violet robe.
  // Deliberately skips headgear:'hood' — that silhouette is reserved for
  // shadow_dealer, where it reads as concealment rather than mysticism.
  arcanist_luna: npc({
    shape: 'slim',
    color: '#dcc8d8', // pale lavender-white, indoor/arcane complexion
    eyes: 'stars',
    mouth: 'smile',
    glow: true,
    humanoid: {
      hair: { style: 'loose', color: '#4a2a5a', streakColor: '#c8a8e8' },
      garment: { kind: 'robe', color: '#3a2a5c', trimColor: '#9a8ab0', underColor: '#6a3a7a' },
    },
  }),

  // ── Hunter Kael — Shop (rugged, sun-weathered, friendly tracker) ──
  // Vendor family #3 of 4: class signature = a bow prop (he sells them, he
  // carries one) in a lighter oak-brown than ranger_cole's dark-wood longbow
  // — and, unlike Cole, an open unhooded face + daylight woodsman green, so
  // the two "bow" characters don't read as the same archetype.
  hunter_kael: npc({
    shape: 'classic', // no other bespoke NPC uses this body shape — Kael reads distinct on silhouette alone
    color: '#bf9468',
    eyes: 'happy',
    mouth: 'grin', // "A fellow tracker!"
    humanoid: {
      hair: { style: 'short', color: '#3a2a18' },
      beard: { style: 'stubble', color: '#3a2a18' },
      garment: { kind: 'tunic', color: '#4a5c3a', trimColor: '#6b4a32' }, // muted woodsman olive-green
      props: [{ kind: 'bow', color: '#8a6a3a' }],
    },
  }),

  // ── Shadow Dealer — Shop (hooded, guarded, no names) ──
  // Vendor family #4 of 4, and the deliberate outlier: hood UP (near-black,
  // plum accent — NOT Cole's cold teal-grey) + a near-black cloak with a
  // brass clasp (ill-gotten-goods touch) + a dark, easy-to-miss satchel.
  // Face-in-shadow reads as concealment here, distinct from Luna's open,
  // star-eyed mysticism — "you didn't see me" made visual.
  shadow_dealer: npc({
    shape: 'slim',
    color: '#b8a8a0', // barely matters — mostly hidden in hood shadow
    eyes: 'round',
    mouth: 'neutral',
    humanoid: {
      headgear: { kind: 'hood', color: '#181418', accentColor: '#3a2438' },
      cloak: { color: '#221a26', claspColor: '#5a3a1a' },
      garment: { kind: 'tunic', color: '#2a2028', trimColor: '#1a1418' },
      props: [{ kind: 'satchel', color: '#2a2028' }],
    },
  }),
};
